import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MainButton } from '../../../styles/button.style';
import { CargoTemperatureConfig } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { SubSectionHeader } from '../../aol/aol-container/AolContainer.style';
import {
    AlternatingRow,
    CargoTemperatureConfigRow,
    CargoesListWrapper,
    ConfigRow,
    InhibitorButton,
    InputFieldsRow,
    InputFieldsString,
} from './CargoTempConfig.style';
import CargoInputFieldsComponent from './cargo-input-fields-component/CargoInputFieldsComponent';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { CargoTemperatureInputField } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureInputField';
import { TankDetails, SortColumnEnum } from '../CargoTemperatureLogUtils';
import { CargoTempCategory } from '../../../types/ViewModel/CargoTempLog/CargoInTankItem';
import { CargoCol, ColHeader } from '../cargo-temp-log-reporting/CargoTempLogReporting.style';
import { useEffect, useState } from 'react';
import { CargoTempLogOxygenUnitEnum } from '../../../enums/cargoTempLogOxygenUnitEnum';
import UpdateInfo from 'src/components/common/UpdateInfo';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import CargoTempQuestionnaire from './CargoTempQuestionnaire';
import CategoryElement from '../cargo-category/CategoryElement';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { useAppStore } from 'src/context/AppContext';
import { isDateSameOrAfter } from 'odfjell-date-time-utils/dateTimeUtils';
import { isInhibitorQuestionnaireCompleted } from 'src/utils/cargo-temp-log/cargoTempLogUtils';

type Props = {
    newCargo: boolean;
    numberOfTimesToReport: number;
    commonOxygenUnitType: string;
    filteredCargoTemperatureConfigs: CargoTemperatureConfig[];
    cargoTanks: TankDetails[];
    setFilteredCargoTemperatureConfigs: (configs: CargoTemperatureConfig[]) => void;
    toggleIsEditFieldsOpen: (conf: CargoTemperatureConfig) => void;
    changeLogTimesPerDay: (changeObj: InputChangeData) => void;
    changeOxygenUnitTypeForChemical: (changeObj: InputChangeData) => void;
};

const LOG_SCHEDULES = {
    1: ['12:00'],
    2: ['08:00', '20:00'],
    3: ['00:00', '08:00', '16:00'],
    6: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
};

const CargoTempConfigList: React.FC<Props> = (props: Props) => {
    const logTimesString = (logTimes: number) => LOG_SCHEDULES[logTimes].reduce((x, logTime) => `${x}, ${logTime}`);
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [sortColumn, setSortColumn] = useState<SortColumnEnum>(SortColumnEnum.CargoNr);
    const [cargoTemperatureConfigsList, setCargoTemperatureConfigsList] = useState<CargoTemperatureConfig[]>([]);
    const [showInhibitorQuestionnaire, setShowInhibitorQuestionnaire] = useState<boolean>(false);
    const [selectedConfig, setSelectedConfig] = useState<CargoTemperatureConfig | undefined>(undefined);
    const [inhibitorQuestionnaireEnabled, setInhibitorQuestionnaireEnabled] = useState<boolean>(false);
    const { checkIsCargoTempLogReadOnly } = useReadOnlyUtils();
    const { appFeatureSettings } = useAppStore();

    useEffect(() => {
        const rows = props.filteredCargoTemperatureConfigs;
        setCargoTemperatureConfigsList([...rows]);
    }, [props.filteredCargoTemperatureConfigs]);

    useEffect(() => {
        if (!props.filteredCargoTemperatureConfigs || !props.cargoTanks) return;
        let rows = props.filteredCargoTemperatureConfigs;
        if (sortColumn === SortColumnEnum.CargoNr) {
            rows = props.filteredCargoTemperatureConfigs.sort((a, b) => {
                return sortDesc ? (a.cargoNumber > b.cargoNumber ? -1 : 1) : a.cargoNumber > b.cargoNumber ? 1 : -1;
            });
        } else if (sortColumn === SortColumnEnum.TradeName) {
            rows = props.filteredCargoTemperatureConfigs.sort((a, b) => {
                return sortDesc ? (a.tradeName > b.tradeName ? -1 : 1) : a.tradeName > b.tradeName ? 1 : -1;
            });
        }

        setCargoTemperatureConfigsList([...rows]);
    }, [sortColumn, sortDesc]);

    useEffect(() => {
        if (appFeatureSettings) {
            const cargoTempLogQuestionnaire = appFeatureSettings.find((x) => x.appFeatureId === AppFeatureEnum.CargoTempLogQuestionnaire);
            if (cargoTempLogQuestionnaire && isDateSameOrAfter(new Date(), cargoTempLogQuestionnaire.fromDate)) {
                setInhibitorQuestionnaireEnabled(true);
            } else {
                setInhibitorQuestionnaireEnabled(false);
            }
        }
    }, [appFeatureSettings]);

    const isReadOnly = checkIsCargoTempLogReadOnly();

    const inputFieldStringName = (inputFields: CargoTemperatureInputField[]) =>
        inputFields
            .sort((a, b) => a.cargoTemperatureInputFieldId - b.cargoTemperatureInputFieldId)
            .reduce((str, inputField, i) => `${str}${inputField.fieldName}${i < inputFields.length - 1 ? ', ' : ''}`, '');

    const onChangeSort = (clickedColumn: SortColumnEnum) => {
        if (clickedColumn === sortColumn) {
            setSortDesc(!sortDesc);
        } else {
            setSortColumn(clickedColumn);
        }
    };

    const getO2Unit = (oxygenUnitType: string): string => {
        if (oxygenUnitType) {
            return oxygenUnitType;
        } else if (!oxygenUnitType && props.commonOxygenUnitType) {
            return props.commonOxygenUnitType;
        } else if (!oxygenUnitType && !props.commonOxygenUnitType) {
            return CargoTempLogOxygenUnitEnum.PerCent;
        }
    };

    const openInhibitorQuestionnaire = (config: CargoTemperatureConfig) => {
        setSelectedConfig(config);
        setShowInhibitorQuestionnaire(true);
    };

    const closeInhibitorQuestionnaire = () => {
        setShowInhibitorQuestionnaire(false);
        setSelectedConfig(undefined);
    };

    const saveInhibitorData = (updatedConfig: CargoTemperatureConfig) => {
        if (!updatedConfig) return;

        // Find the config in the list and update it
        const configIndex = props.filteredCargoTemperatureConfigs.findIndex((c) => c.cargoNumber === updatedConfig.cargoNumber);

        if (configIndex !== -1) {
            const updatedConfigs = [...props.filteredCargoTemperatureConfigs];
            updatedConfigs[configIndex] = updatedConfig;
            props.setFilteredCargoTemperatureConfigs(updatedConfigs);
        }

        setShowInhibitorQuestionnaire(false);
        setSelectedConfig(undefined);
    };

    return (
        <CargoesListWrapper key={`${props.newCargo ? 'n' : 'c'}_configs`}>
            {!props.newCargo && <SubSectionHeader>Configured cargoes</SubSectionHeader>}
            <CargoTemperatureConfigRow numberOfTimesToReport={props.numberOfTimesToReport} className="is-header">
                <h4 />
                <CargoCol className="header cargo-nr" onClick={() => onChangeSort(SortColumnEnum.CargoNr)}>
                    Cargo nr
                    {sortColumn === SortColumnEnum.CargoNr && (
                        <span>
                            {' '}
                            <FontAwesomeIcon
                                className="sort-icon"
                                title="Click to change sorting"
                                icon={sortDesc && sortColumn === SortColumnEnum.CargoNr ? 'arrow-up' : 'arrow-down'}
                            />
                        </span>
                    )}
                </CargoCol>
                <CargoCol className="header trade-name" onClick={() => onChangeSort(SortColumnEnum.TradeName)}>
                    Trade name
                    {sortColumn === SortColumnEnum.TradeName && (
                        <span>
                            {' '}
                            <FontAwesomeIcon
                                className="sort-icon"
                                title="Click to change sorting"
                                icon={sortDesc && sortColumn === SortColumnEnum.TradeName ? 'arrow-up' : 'arrow-down'}
                            />
                        </span>
                    )}
                </CargoCol>
                <ColHeader>
                    <h4>Category</h4>
                </ColHeader>
                <ColHeader>
                    <h4>Log schedule</h4>
                </ColHeader>
                <ColHeader>
                    <h4>Selected inputs</h4>
                </ColHeader>
                <ColHeader>
                    <h4>Update info</h4>
                </ColHeader>
            </CargoTemperatureConfigRow>
            {cargoTemperatureConfigsList
                .filter((c) => (props.newCargo ? !c?.cargoTemperatureConfigId : c?.cargoTemperatureConfigId))
                .map((config, i) => (
                    <AlternatingRow key={`${props.newCargo ? 'n' : 'c'}_configs_${config.cargoNumber}_${i}`}>
                        <CargoTemperatureConfigRow numberOfTimesToReport={props.numberOfTimesToReport}>
                            <MainButton title="Edit inputs" onClick={() => props.toggleIsEditFieldsOpen(config)}>
                                <FontAwesomeIcon icon="pencil-alt" />
                            </MainButton>
                            <div style={{ display: 'flex' }}>
                                {!config.cargoTemperatureConfigId && <b>NEW </b>}
                                {config.cargoNumber}
                            </div>
                            <div>{config.tradeName}</div>
                            <div>
                                <CategoryElement cargoTanks={props.cargoTanks} cargoNumber={config.cargoNumber} />
                                {props.cargoTanks.find((tank) => tank.cargoNumber === config.cargoNumber)?.category === CargoTempCategory.Inhibited &&
                                    inhibitorQuestionnaireEnabled && (
                                        <InhibitorButton
                                            questionnaireCompleted={isInhibitorQuestionnaireCompleted(config)}
                                            title="Inhibitor details"
                                            onClick={() => openInhibitorQuestionnaire(config)}
                                            style={{ marginLeft: '5px' }}
                                        >
                                            Inhibitor Configuration
                                        </InhibitorButton>
                                    )}
                            </div>
                            {config.isEditFieldsOpen ? (
                                <>
                                    <ConfigRow>
                                        <InputFieldsString>{logTimesString(config?.logTimesPerDay ?? props.numberOfTimesToReport)}</InputFieldsString>
                                    </ConfigRow>
                                    <ConfigRow>
                                        <InputFieldsString>{getO2Unit(config?.oxygenUnitType)}</InputFieldsString>
                                    </ConfigRow>
                                    <ConfigRow>
                                        <InputFieldsString>{inputFieldStringName(config.inputFields)}</InputFieldsString>
                                    </ConfigRow>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <OdfjellInputComponent
                                            key={`${props.newCargo ? 'n' : 'c'}_logTimeSelect_${i}`}
                                            type={'select'}
                                            name={'LogTimeSelector'}
                                            isReadOnly={isReadOnly}
                                            value={config?.logTimesPerDay ?? props.numberOfTimesToReport}
                                            onChange={props.changeLogTimesPerDay}
                                            options={Object.keys(LOG_SCHEDULES)}
                                            dataObject={{ config, configIndex: i }}
                                        />

                                        {(config?.logTimesPerDay ?? props.numberOfTimesToReport) &&
                                            LOG_SCHEDULES[config?.logTimesPerDay ?? props.numberOfTimesToReport].map((logTime, j) => (
                                                <div key={`${props.newCargo ? 'n' : 'c'}_logtime_${config.cargoNumber}_${i}_${j}`}>{logTime}</div>
                                            ))}
                                    </div>
                                    <div>
                                        <CargoInputFieldsComponent
                                            keyName={`${props.newCargo ? 'n' : 'c'}_fields_${i}`}
                                            cargoTemperatureConfig={config}
                                            filteredCargoTemperatureConfigs={props.filteredCargoTemperatureConfigs}
                                            setFilteredCargoTemperatureConfigs={props.setFilteredCargoTemperatureConfigs}
                                        ></CargoInputFieldsComponent>
                                        <InputFieldsRow>
                                            <label>Oxygen unit</label>
                                            <OdfjellInputComponent
                                                key={`${props.newCargo ? 'n' : 'c'}_oxygenUnitTypeSelect_${i}`}
                                                type={'select'}
                                                name={'OxygenUnitTypeSelector'}
                                                isReadOnly={isReadOnly}
                                                value={config?.oxygenUnitType ?? props.commonOxygenUnitType}
                                                onChange={props.changeOxygenUnitTypeForChemical}
                                                options={Object.values(CargoTempLogOxygenUnitEnum)}
                                                dataObject={{ config, configIndex: i }}
                                            />
                                        </InputFieldsRow>
                                    </div>
                                    <div>
                                        <UpdateInfo
                                            inline={true}
                                            lastUpdatedUtc={config.lastUpdatedUtc}
                                            lastUpdatedBy={config.lastUpdatedBy}
                                        ></UpdateInfo>
                                    </div>
                                </>
                            )}
                        </CargoTemperatureConfigRow>
                    </AlternatingRow>
                ))}
            {showInhibitorQuestionnaire && selectedConfig && (
                <CargoTempQuestionnaire config={selectedConfig} onClose={closeInhibitorQuestionnaire} onSave={saveInhibitorData} />
            )}
        </CargoesListWrapper>
    );
};

export default CargoTempConfigList;
